import React, { useState } from 'react';
import axios from 'axios';
import ReCAPTCHA from "react-google-recaptcha";

// `embedded` drops the card chrome when the form is rendered inside the mobile sheet
const ApplyJob = ({ jobId, jobTitle, embedded = false, onClose }) => {
    // --- STATE MANAGEMENT ---
    const [experiences, setExperiences] = useState([{ institute_name: '', designation: '', period: '' }]);
    const [certifications, setCertifications] = useState([{ title: '', details: '' }]);
    const [cvFile, setCvFile] = useState(null);
    const [cvError, setCvError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [eduLevel, setEduLevel] = useState('Graduate');

    const [captchaToken, setCaptchaToken] = useState(null);

    const [phone, setPhone] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [submitError, setSubmitError] = useState('');

    // --- BD MOBILE NUMBER VALIDATION ---
    // Accepts 01XXXXXXXXX, 8801XXXXXXXXX or +8801XXXXXXXXX (operator digit 3-9)
    const BD_MOBILE_REGEX = /^(?:\+?880|0)1[3-9]\d{8}$/;

    // Strips spaces/dashes and returns the local 01XXXXXXXXX form
    const normalizePhone = (value) => {
        const cleaned = value.replace(/[\s-]/g, '');
        return cleaned.replace(/^(?:\+?880)/, '0');
    };

    const validatePhone = (value) => {
        const cleaned = value.replace(/[\s-]/g, '');
        if (!cleaned) return "Phone number is required.";
        if (!BD_MOBILE_REGEX.test(cleaned)) {
            return "Enter a valid Bangladeshi mobile number (e.g. 01712345678).";
        }
        return '';
    };

    const handlePhoneChange = (e) => {
        // Allow only digits and a leading +
        const value = e.target.value.replace(/(?!^\+)[^\d]/g, '');
        setPhone(value);
        if (phoneError) setPhoneError(validatePhone(value));
    };

    // --- CV VALIDATION (PDF ONLY) ---
    // Reads the first bytes so a renamed .docx/.jpg can't pass as a PDF
    const hasPdfSignature = (file) => new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
            const bytes = new Uint8Array(reader.result);
            const signature = String.fromCharCode(...bytes);
            resolve(signature === '%PDF-');
        };
        reader.onerror = () => resolve(false);
        reader.readAsArrayBuffer(file.slice(0, 5));
    });

    const handleCvChange = async (e) => {
        const file = e.target.files[0];
        setCvFile(null);
        setCvError('');

        if (!file) return;

        if (!file.name.toLowerCase().endsWith('.pdf')) {
            setCvError("Only PDF files are accepted. Please upload your CV as a PDF.");
            e.target.value = '';
            return;
        }

        if (file.size === 0) {
            setCvError("This file is empty. Please upload a valid PDF.");
            e.target.value = '';
            return;
        }

        if (!(await hasPdfSignature(file))) {
            setCvError("This file is not a valid PDF. Please upload a real PDF document.");
            e.target.value = '';
            return;
        }

        setCvFile(file);
    };

    // --- EXPERIENCE DYNAMIC HANDLERS ---
    const handleExpChange = (index, field, value) => {
        const updated = [...experiences];
        updated[index][field] = value;
        setExperiences(updated);
    };

    const addRow = () => setExperiences([...experiences, { institute_name: '', designation: '', period: '' }]);

    const removeRow = (index) => {
        if (experiences.length > 1) {
            setExperiences(experiences.filter((_, i) => i !== index));
        }
    };

    // --- CERTIFICATION DYNAMIC HANDLERS ---
    const handleCertChange = (index, field, value) => {
        const updated = [...certifications];
        updated[index][field] = value;
        setCertifications(updated);
    };

    const addCertRow = () => setCertifications([...certifications, { title: '', details: '' }]);

    const removeCertRow = (index) => {
        if (certifications.length > 1) {
            setCertifications(certifications.filter((_, i) => i !== index));
        }
    };

    const handleCaptchaChange = (token) => {
        setCaptchaToken(token);
    };

    // Clears everything so a second application starts from a blank form
    const resetForm = () => {
        setExperiences([{ institute_name: '', designation: '', period: '' }]);
        setCertifications([{ title: '', details: '' }]);
        setCvFile(null);
        setCvError('');
        setEduLevel('Graduate');
        setPhone('');
        setPhoneError('');
        setSubmitError('');
        setCaptchaToken(null);
        setIsSubmitted(false);
    };

    // --- FORM SUBMISSION ---
    const handleSubmit = async (e) => {
        e.preventDefault();

        setSubmitError('');

        const phoneValidationError = validatePhone(phone);
        if (phoneValidationError) {
            setPhoneError(phoneValidationError);
            e.target.phone.focus();
            return;
        }

        if (!cvFile) {
            setCvError("Please attach your CV as a PDF file.");
            setSubmitError("Please attach your CV as a PDF file before submitting.");
            return;
        }

        if (!captchaToken) {
            setSubmitError("Please verify that you are not a robot.");
            return;
        }

        setIsSubmitting(true);
        const formData = new FormData();
        const f = e.target;

        // 1. Core Identity
        formData.append('job', jobId); 
        formData.append('full_name', f.full_name.value);
        formData.append('email', f.email.value);
        formData.append('phone', normalizePhone(phone));
        formData.append('present_address', f.present_address.value);
        formData.append('highest_education', eduLevel);
        formData.append('gender', f.gender.value);
        formData.append('nationality', f.nationality.value);
        formData.append('id_no', f.id_no.value);

        // 2. Education Mapping (Must match Serializer field names)
        formData.append('undergraduate_university_name', f.ug_uni.value);
        formData.append('undergraduate_subject', f.ug_sub.value);
        formData.append('undergraduate_result', f.ug_res.value);

        formData.append('hsc_or_a_level', f.hsc_type.value);
        formData.append('college_name', f.hsc_inst.value);
        formData.append('hsc_or_a_level_result', f.hsc_res.value);

        formData.append('ssc_or_o_level', f.ssc_type.value);
        formData.append('school_name', f.ssc_inst.value);
        formData.append('ssc_or_o_level_result', f.ssc_res.value);

        // Post Graduate (Conditional)
        if (eduLevel === "Post Graduate") {
            formData.append('post_graduate_university', f.pg_uni.value || '');
            formData.append('post_graduate_subject', f.pg_sub.value || '');
            formData.append('post_graduate_result', f.pg_res.value || '');
        }

        // 3. Certifications (Optional — drop empty rows, keep the ones with a title)
        const validCertifications = certifications.filter(c => c.title.trim() !== "");
        formData.append('certifications_json', JSON.stringify(validCertifications));
        
        // 4. Files
        if (cvFile) formData.append('cv_attachment', cvFile);

        // 5. Clean Experiences (Remove empty rows before stringifying)
        const validExperiences = experiences.filter(ex => ex.institute_name.trim() !== "");
        formData.append('experiences_json', JSON.stringify(validExperiences));
        formData.append('experience', validExperiences.length > 0 ? 'true' : 'false');

        formData.append("recaptcha_token", captchaToken);

        axios.defaults.xsrfCookieName = 'csrftoken';
        axios.defaults.xsrfHeaderName = 'X-CSRFToken';
        axios.defaults.withCredentials = true;
        try {
            await axios.post('/api/apply/', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setIsSubmitted(true);
        } catch (err) {
            console.error("Submission Error Details:", err.response?.data);
            const errorMsg = err.response?.data
                ? Object.entries(err.response.data).map(([k, v]) => `${k}: ${v}`).join(' · ')
                : "We could not reach the server. Please check your connection and try again.";
            setSubmitError(errorMsg);
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- REUSABLE STYLES ---
    const shellStyle = embedded
        ? "pb-2"
        : "bg-green-950/90 backdrop-blur-2xl border border-white/10 p-6 sm:p-8 md:p-12 rounded-[28px] sm:rounded-[40px] shadow-2xl lg:max-h-[80vh] lg:overflow-y-auto custom-scrollbar";
    const inputStyle ="bg-white/10 p-4 rounded-2xl border border-white/5 outline-none focus:border-emerald-500 text-white placeholder:text-white/40 w-full transition-all duration-300";
    const labelStyle = "text-[10px] font-black text-emerald-500 uppercase tracking-[0.2em] mb-2 block";

    if (isSubmitted) {
        return (
            <div className={embedded ? "" : "bg-green-950/90 backdrop-blur-2xl border border-white/10 p-6 sm:p-8 md:p-12 rounded-[28px] sm:rounded-[40px] shadow-2xl"}>
                <div className="text-center py-6 sm:py-8">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-5 sm:mb-6 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center text-emerald-500 text-3xl">
                        ✓
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-black uppercase mb-3 tracking-tighter">Application Sent</h2>
                    <p className="text-sm text-white/60 leading-relaxed max-w-sm mx-auto">
                        Thank you for applying for <span className="text-orange-500 font-bold">{jobTitle}</span>.
                        We have received your application and our team will get back to you if your profile matches the role.
                    </p>
                    <div className="mt-8 space-y-4">
                        {onClose && (
                            <button
                                type="button"
                                onClick={onClose}
                                className="w-full bg-emerald-500 text-green-950 font-black py-4 rounded-full uppercase tracking-[0.15em] text-xs active:scale-[0.98] transition-transform"
                            >
                                Back to jobs
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={resetForm}
                            className="block mx-auto text-[10px] font-bold text-emerald-500 hover:text-white transition-colors tracking-widest uppercase"
                        >
                            Submit another application
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={shellStyle}>
            {/* The sheet already shows the job title in its header */}
            {!embedded && (
                <>
                    <h2 className="text-2xl sm:text-3xl font-black uppercase mb-1 tracking-tighter">Apply for</h2>
                    <h3 className="text-lg sm:text-xl font-bold text-orange-500 mb-6 sm:mb-8 break-words">{jobTitle}</h3>
                </>
            )}

            <form onSubmit={handleSubmit} className="space-y-8 sm:space-y-10">
                
                {/* PERSONAL SECTION */}
                <div className="space-y-4">
                    <span className={labelStyle}>Personal Information</span>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input name="full_name" placeholder="Full Name" autoComplete="name" required className={inputStyle} />
                        <input name="email" type="email" inputMode="email" autoComplete="email" placeholder="Email Address" required className={inputStyle} />
                        <div>
                            <input
                                name="phone"
                                type="tel"
                                inputMode="tel"
                                autoComplete="tel"
                                maxLength={14}
                                placeholder="Phone Number (e.g. 01712345678)"
                                required
                                value={phone}
                                onChange={handlePhoneChange}
                                onBlur={() => setPhoneError(validatePhone(phone))}
                                className={`${inputStyle} ${phoneError ? 'border-red-500 focus:border-red-500' : ''}`}
                            />
                            {phoneError && (
                                <p className="text-[11px] text-red-400 mt-2 ml-1">{phoneError}</p>
                            )}
                        </div>
                        <input name="id_no" placeholder="NID / Passport Number" required className={inputStyle} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <input name="nationality" placeholder="Nationality" required className={inputStyle} />
                        <select name="gender" className={inputStyle}>
                            <option value="Male" className="bg-[#0a1a0d]">Male</option>
                            <option value="Female" className="bg-[#0a1a0d]">Female</option>
                            <option value="Other" className="bg-[#0a1a0d]">Other</option>
                        </select>
                    </div>
                    <input name="present_address" placeholder="Present Address" autoComplete="street-address" required className={inputStyle} />
                </div>

                {/* EDUCATION SECTION */}
                <div className="space-y-6 pt-6 border-t border-white/10">
                    <span className={labelStyle}>Academic History</span>
                    
                    <div>
                        <p className="text-xs text-white/40 mb-2">Highest Education Level</p>
                        <select 
                            value={eduLevel} 
                            onChange={(e) => setEduLevel(e.target.value)} 
                            className={inputStyle}
                        >
                            <option value="Post Graduate" className="bg-[#0a1a0d]">Post Graduate</option>
                            <option value="Graduate" className="bg-[#0a1a0d]">Graduate</option>
                            <option value="HSC/A-Level" className="bg-[#0a1a0d]">HSC / A-Level</option>
                            <option value="SSC/O-Level" className="bg-[#0a1a0d]">SSC / O-Level</option>
                        </select>
                    </div>

                    {eduLevel === "Post Graduate" && (
                        <div className="p-5 bg-emerald-500/5 rounded-3xl border border-emerald-500/20">
                            <p className="text-xs font-bold text-emerald-500 mb-3">Post Graduate Details</p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <input name="pg_uni" placeholder="University" required className={inputStyle} />
                                <input name="pg_sub" placeholder="Subject" required className={inputStyle} />
                                <input name="pg_res" placeholder="Result" required className={inputStyle} />
                            </div>
                        </div>
                    )}

                    <div className="space-y-3">
                        <p className="text-xs text-white/40">Undergraduate Details</p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <input name="ug_uni" placeholder="University" required className={inputStyle} />
                            <input name="ug_sub" placeholder="Subject" required className={inputStyle} />
                            <input name="ug_res" placeholder="Result" required className={inputStyle} />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                        <div className="space-y-3">
                            <select name="hsc_type" className={inputStyle}>
                                <option value="HSC">HSC</option>
                                <option value="A Level">A Level</option>
                            </select>
                            <input name="hsc_inst" placeholder="College Name" required className={inputStyle} />
                            <input name="hsc_res" placeholder="GPA/Result" required className={inputStyle} />
                        </div>
                        <div className="space-y-3">
                            <select name="ssc_type" className={inputStyle}>
                                <option value="SSC">SSC</option>
                                <option value="O Level">O Level</option>
                            </select>
                            <input name="ssc_inst" placeholder="School Name" required className={inputStyle} />
                            <input name="ssc_res" placeholder="GPA/Result" required className={inputStyle} />
                        </div>
                    </div>
                </div>

                {/* EXPERIENCE SECTION */}
                <div className="space-y-4 pt-6 border-t border-white/10">
                    <span className={labelStyle}>Work Experience</span>
                    {experiences.map((exp, i) => (
                        <div key={i} className="flex gap-3 group items-start">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 flex-grow bg-white/5 p-4 rounded-3xl border border-white/5">
                                <input placeholder="Company" className="bg-transparent text-base sm:text-sm outline-none text-white placeholder:text-white/40" value={exp.institute_name} onChange={(e) => handleExpChange(i, 'institute_name', e.target.value)} />
                                <input placeholder="Designation" className="bg-transparent text-base sm:text-sm outline-none text-white placeholder:text-white/40" value={exp.designation} onChange={(e) => handleExpChange(i, 'designation', e.target.value)} />
                                <input placeholder="Period" className="bg-transparent text-base sm:text-sm outline-none text-white placeholder:text-white/40" value={exp.period} onChange={(e) => handleExpChange(i, 'period', e.target.value)} />
                            </div>
                            {experiences.length > 1 && (
                                <button type="button" onClick={() => removeRow(i)} className="mt-4 text-white/20 hover:text-red-500 transition-colors">✕</button>
                            )}
                        </div>
                    ))}
                    <button type="button" onClick={addRow} className="text-[10px] font-bold text-emerald-500 hover:text-white transition-colors tracking-widest">+ ADD HISTORY</button>
                </div>

                {/* CERTIFICATIONS SECTION (Optional, repeatable) */}
                <div className="space-y-4 pt-6 border-t border-white/10">
                    <span className={labelStyle}>Certifications (Optional)</span>
                    {certifications.map((cert, i) => (
                        <div key={i} className="flex gap-3 group items-start">
                            <div className="space-y-3 flex-grow bg-white/5 p-4 rounded-3xl border border-white/5">
                                <input
                                    placeholder="Certification Title (e.g. CEH, OSCP)"
                                    className="bg-transparent text-base sm:text-sm outline-none text-white placeholder:text-white/40 w-full"
                                    value={cert.title}
                                    onChange={(e) => handleCertChange(i, 'title', e.target.value)}
                                />
                                <textarea
                                    placeholder="Briefly describe this certification..."
                                    className="bg-transparent text-base sm:text-sm outline-none text-white placeholder:text-white/40 w-full h-16 resize-none"
                                    value={cert.details}
                                    onChange={(e) => handleCertChange(i, 'details', e.target.value)}
                                />
                            </div>
                            {certifications.length > 1 && (
                                <button type="button" onClick={() => removeCertRow(i)} className="mt-4 text-white/20 hover:text-red-500 transition-colors">✕</button>
                            )}
                        </div>
                    ))}
                    <button type="button" onClick={addCertRow} className="text-[10px] font-bold text-emerald-500 hover:text-white transition-colors tracking-widest">+ ADD CERTIFICATION</button>
                </div>

                {/* ATTACHMENT SECTION */}
                <div className="space-y-4 pt-6 border-t border-white/10">
                    <span className={labelStyle}>Resume / CV</span>
                    <div className={`bg-emerald-500/5 border border-dashed p-6 rounded-3xl text-center ${cvError ? 'border-red-500/50' : 'border-emerald-500/30'}`}>
                        <input
                            type="file"
                            id="cv-upload"
                            accept="application/pdf,.pdf"
                            required
                            className="hidden"
                            onChange={handleCvChange}
                        />
                        <label htmlFor="cv-upload" className="cursor-pointer block text-sm font-bold text-emerald-500 hover:text-white transition-colors break-all">
                            {cvFile ? cvFile.name : "CHOOSE PDF FILE"}
                        </label>
                        <p className="text-[10px] text-white/40 mt-2">PDF only</p>
                    </div>
                    {cvError && (
                        <p className="text-[11px] text-red-400 ml-1">{cvError}</p>
                    )}
                </div>

                {/* The reCAPTCHA widget is a fixed 304px, so scale it down on narrow phones */}
                <div className="flex justify-center pt-6 overflow-hidden">
                {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
                    <div className="origin-top scale-[0.85] xs:scale-100">
                        <ReCAPTCHA
                            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                            onChange={handleCaptchaChange}
                        />
                    </div>
                )}
                </div>

                {submitError && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4 text-red-300 text-xs leading-relaxed break-words">
                        {submitError}
                    </div>
                )}

                <button
                    disabled={isSubmitting}
                    className="w-full bg-orange-700/90 text-white font-black py-5 sm:py-6 rounded-full uppercase tracking-[0.15em] sm:tracking-[0.2em] text-xs sm:text-[13px] hover:bg-white hover:text-green-950 transition-all duration-500 disabled:opacity-30"
                >
                    {isSubmitting ? 'Processing...' : 'Submit Application'}
                </button>
            </form>
        </div>
    );
};

export default ApplyJob;